/**
 * SHARED GRID CELL RENDERERS
 *
 * Renders highlighted and linked values used by schema-driven CRM data grids.
 *
 * Changes:
 * - 2026-08-25: Makes nested renderer text shrink to one line with an ellipsis.
 */

import { highlightText } from './highlightUtils';
import { renderTypeChip } from './typeChipUtils';

const ELLIPSIS_CLASS_NAME = 'min-w-0 max-w-full overflow-hidden text-ellipsis whitespace-nowrap';

export const createHighlightCellRenderer = (searchQuery: string) => {
  return (params: { value: any }) => {
    const value = params.value?.toString() || '';
    const keywords = searchQuery.split(' ').filter(Boolean);
    return highlightText(value, keywords);
  };
};

export const createNameCellRenderer = (searchQuery: string) => {
  return (params: { value: any; data: { rlpCompanyId?: string | null; teamId?: string | null } }) => {
    const value = params.value?.toString() || '';
    const keywords = searchQuery.split(' ').filter(Boolean);
    const rlpCompanyId = params.data.rlpCompanyId ? Number(params.data.rlpCompanyId) : 0;
    const teamId = params.data.teamId ? Number(params.data.teamId) : 0;

    return (
      <div className="flex min-w-0 max-w-full items-center gap-2 overflow-hidden">
        {renderTypeChip({ rlpCompanyId, teamId })}
        <span className={ELLIPSIS_CLASS_NAME}>{highlightText(value, keywords)}</span>
      </div>
    );
  };
};

export const createAddressCellRenderer = (searchQuery: string) => {
  return (params: { value: any }) => {
    const address = params.value;
    if (!address || typeof address !== 'string') return '';
    const cleanAddress = address.replace(/\s+/g, ' ').trim().replace(/[,\s]+$/, '');
    const keywords = searchQuery.split(' ').filter(Boolean);
    return highlightText(cleanAddress, keywords);
  };
};

export const createEmailCellRenderer = () => {
  return (params: { value: any }) => {
    if (!params.value) return null;
    const value = params.value;
    return (
      <a href={`mailto:${value}`} className={`${ELLIPSIS_CLASS_NAME} block text-blue-600 hover:underline`}>
        {value}
      </a>
    );
  };
};

export const createUrlCellRenderer = () => {
  return (params: { value: any }) => {
    if (!params.value) return null;
    const value = params.value;
    return (
      <a href={value} target="_blank" rel="noopener noreferrer" className={`${ELLIPSIS_CLASS_NAME} block text-blue-600 hover:underline`}>
        {value}
      </a>
    );
  };
};

export const createTelCellRenderer = () => {
  return (params: { value: any }) => {
    if (!params.value) return null;
    const value = params.value;
    return (
      <a href={`tel:${value}`} className={`${ELLIPSIS_CLASS_NAME} block text-blue-600 hover:underline`}>
        {value}
      </a>
    );
  };
};
